import React from 'react'
import { Link } from '@ally/metronome-ui'
import {
  muiInProgress,
  muiWarningFill,
  muiCancelOutline,
  muiSuccessOutline,
} from '@ally/metronome-icons'
import { formatDate } from '../../utils'
import {
  ActivityTypes,
  WireStatuses,
  AchStatuses,
  CheckOrderStatuses,
  StatusOpts,
} from './types'

const wireTransferStatuses: Record<WireStatuses, StatusOpts> = {
  [WireStatuses.Requested]: {
    description: (): string =>
      "You requested a wire transfer. Next, we'll review it and let you know if we have any questions.",
    icon: muiInProgress,
  },
  [WireStatuses.Reviewing]: {
    description: (): string =>
      "We're reviewing your wire request to make sure we don't need any further information from you.",
    icon: muiInProgress,
  },
  [WireStatuses.Processing]: {
    description: (): string =>
      "We're processing your wire request and will let you know when we deliver your funds.",
    icon: muiInProgress,
  },
  [WireStatuses.VerificationRequired]: {
    description: (): React.ReactElement => {
      return (
        <>
          Action Required on your wire request: We need more information from
          you. Call us at{' '}
          <Link
            text="1-877-247-2559"
            to="tel:18772472559"
            aria-label="1, 8 7 7, 2 4 7, 2 5 5 9"
          />
          .
        </>
      )
    },
    icon: muiWarningFill,
    iconColor: 'warning',
  },
  [WireStatuses.Canceled]: {
    description: (date): string =>
      `We canceled your wire transfer on ${formatDate(
        date as string,
        'MMM DD',
      )}.`,
    icon: muiCancelOutline,
    iconColor: 'slate-3',
  },
  [WireStatuses.Sent]: {
    description: (): string => 'We sent your wire transfer.',
    icon: muiSuccessOutline,
    iconColor: 'success',
  },
}

const achTransferStatuses: Record<AchStatuses, StatusOpts> = {
  [AchStatuses.InProcess]: {
    description: (): string => 'We’re processing your transfer.',
    icon: muiInProgress,
  },
  [AchStatuses.Completed]: {
    description: (): string => "We've delivered your funds.",
    icon: muiSuccessOutline,
    iconColor: 'success',
  },
}

const checkOrderStatuses: Record<CheckOrderStatuses, StatusOpts> = {
  [CheckOrderStatuses.Confirmed]: {
    description: (): string =>
      'You ordered checks and they should ship within 10 days of your order.',
    icon: muiInProgress,
  },
  [CheckOrderStatuses.InProcess]: {
    description: (): string =>
      'You ordered checks and they should ship within 10 days of your order.',
    icon: muiInProgress,
  },
  [CheckOrderStatuses.Shipped]: {
    description: (): string =>
      'Your checks have shipped and you should receive them soon.',
    icon: muiSuccessOutline,
    iconColor: 'success',
  },
}

interface ActivityTypesWithStatuses {
  [ActivityTypes.WireTransfer]: Record<WireStatuses, StatusOpts>
  [ActivityTypes.ACHTransfer]: Record<AchStatuses, StatusOpts>
  [ActivityTypes.CheckOrder]: Record<CheckOrderStatuses, StatusOpts>
}

export const STATUS_TRACKER_ACTIVITY: ActivityTypesWithStatuses = {
  [ActivityTypes.WireTransfer]: wireTransferStatuses,
  [ActivityTypes.ACHTransfer]: achTransferStatuses,
  [ActivityTypes.CheckOrder]: checkOrderStatuses,
}

export const OMITTED_STATUSES = ['Skipped', 'Failed', 'Declined', 'Canceled']
